import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, Users, Zap, Activity } from 'lucide-react';
import { format, subDays, startOfDay } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

interface CostLog {
  id: string;
  user_id: string;
  created_at: string;
  model: string;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  estimated_cost_usd: string | number;
  success: boolean;
  username?: string;
}

interface CostStats {
  totalCost: number;
  totalRequests: number;
  averageCost: number;
  totalTokens: number;
  costToday: number;
  requestsToday: number;
  costThisMonth: number;
}

interface ModelBreakdown {
  model: string;
  count: number;
  cost: number;
  tokens: number;
}

interface DailyUsage {
  date: string;
  cost: number;
  requests: number;
}

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--secondary))',
  'hsl(142, 76%, 36%)',
  'hsl(38, 92%, 50%)',
  'hsl(280, 65%, 60%)',
  'hsl(200, 80%, 50%)',
];

const formatModelName = (model: string): string => {
  return model
    .replace('google/', '')
    .replace('gpt-', 'GPT-')
    .replace('-preview', '')
    .replace('-flash', ' Flash')
    .replace('-pro', ' Pro')
    .replace('-mini', ' Mini');
};

export function AICostTracker() {
  const [logs, setLogs] = useState<CostLog[]>([]);
  const [stats, setStats] = useState<CostStats>({
    totalCost: 0,
    totalRequests: 0,
    averageCost: 0,
    totalTokens: 0,
    costToday: 0,
    requestsToday: 0,
    costThisMonth: 0,
  });
  const [modelBreakdown, setModelBreakdown] = useState<ModelBreakdown[]>([]);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCostData();
  }, []);

  const fetchCostData = async () => {
    setLoading(true);
    try {
      // Fetch recent logs with user info
      const { data: logsData, error: logsError } = await supabase
        .from('ai_recommendation_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (logsError) throw logsError;

      // Fetch usernames separately
      const userIds = [...new Set(logsData?.map(log => log.user_id) || [])];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', userIds);

      const profilesMap = new Map(profilesData?.map(p => [p.id, p.username]) || []);

      // Enrich logs with usernames
      const enrichedLogs = logsData?.map(log => ({
        ...log,
        username: profilesMap.get(log.user_id) || 'unknown'
      })) || [];

      // Calculate stats
      const now = new Date();
      const todayStart = startOfDay(now);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      const allLogs = enrichedLogs;
      const todayLogs = allLogs.filter(log => new Date(log.created_at) >= todayStart);
      const monthLogs = allLogs.filter(log => new Date(log.created_at) >= monthStart);

      setStats({
        totalCost: allLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0),
        totalRequests: allLogs.length,
        averageCost: allLogs.length > 0 
          ? allLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0) / allLogs.length 
          : 0,
        totalTokens: allLogs.reduce((sum, log) => sum + log.total_tokens, 0),
        costToday: todayLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0),
        requestsToday: todayLogs.length,
        costThisMonth: monthLogs.reduce((sum, log) => sum + Number(log.estimated_cost_usd), 0),
      });

      // Calculate model breakdown
      const modelMap = new Map<string, ModelBreakdown>();
      allLogs.forEach(log => {
        const existing = modelMap.get(log.model) || { model: log.model, count: 0, cost: 0, tokens: 0 };
        existing.count += 1;
        existing.cost += Number(log.estimated_cost_usd);
        existing.tokens += log.total_tokens;
        modelMap.set(log.model, existing);
      });
      setModelBreakdown(Array.from(modelMap.values()).sort((a, b) => b.cost - a.cost));

      // Calculate daily usage for last 7 days
      const dailyMap = new Map<string, DailyUsage>();
      for (let i = 6; i >= 0; i--) {
        const date = format(subDays(now, i), 'MMM d');
        dailyMap.set(date, { date, cost: 0, requests: 0 });
      }
      
      allLogs.forEach(log => {
        const date = format(new Date(log.created_at), 'MMM d');
        const existing = dailyMap.get(date);
        if (existing) {
          existing.cost += Number(log.estimated_cost_usd);
          existing.requests += 1;
        }
      });
      setDailyUsage(Array.from(dailyMap.values()));

      setLogs(allLogs.slice(0, 50));
    } catch (error) {
      console.error('Error fetching cost data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-muted rounded animate-pulse" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const pieData = modelBreakdown.map(m => ({
    name: formatModelName(m.model),
    value: m.cost,
    requests: m.count,
  }));

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Total Cost
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.totalCost.toFixed(4)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Last 100 requests
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              This Month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.costThisMonth.toFixed(4)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(new Date(), 'MMMM yyyy')}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Total Requests
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalRequests}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.requestsToday} today
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Avg Cost/Request
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.averageCost.toFixed(5)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              ~{Math.round(stats.totalTokens / Math.max(stats.totalRequests, 1))} tokens
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Breakdown Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Cost by Model
            </CardTitle>
            <CardDescription>
              Breakdown of AI costs by model
            </CardDescription>
          </CardHeader>
          <CardContent>
            {modelBreakdown.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {pieData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [`$${value.toFixed(4)}`, 'Cost']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Usage Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Daily Usage (Last 7 Days)
            </CardTitle>
            <CardDescription>
              Cost and request volume over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dailyUsage.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyUsage}>
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value: number, name: string) => [
                        name === 'cost' ? `$${value.toFixed(4)}` : value,
                        name === 'cost' ? 'Cost' : 'Requests'
                      ]}
                    />
                    <Legend />
                    <Bar yAxisId="right" dataKey="requests" fill="hsl(var(--secondary))" name="Requests" />
                    <Bar yAxisId="left" dataKey="cost" fill="hsl(var(--primary))" name="Cost ($)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Model Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>Model Usage Details</CardTitle>
          <CardDescription>
            Detailed breakdown by AI model
          </CardDescription>
        </CardHeader>
        <CardContent>
          {modelBreakdown.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No model data available
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">Model</th>
                    <th className="text-right py-3 px-2 font-medium">Requests</th>
                    <th className="text-right py-3 px-2 font-medium">Total Cost</th>
                    <th className="text-right py-3 px-2 font-medium">Avg Cost</th>
                    <th className="text-right py-3 px-2 font-medium">Total Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {modelBreakdown.map((model) => (
                    <tr key={model.model} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-3 px-2">
                        <span className="font-medium">{formatModelName(model.model)}</span>
                        <span className="text-xs text-muted-foreground ml-2">({model.model})</span>
                      </td>
                      <td className="text-right py-3 px-2">{model.count.toLocaleString()}</td>
                      <td className="text-right py-3 px-2 font-mono">${model.cost.toFixed(4)}</td>
                      <td className="text-right py-3 px-2 font-mono text-muted-foreground">
                        ${(model.cost / model.count).toFixed(5)}
                      </td>
                      <td className="text-right py-3 px-2 text-muted-foreground">
                        {model.tokens.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent API Calls</CardTitle>
          <CardDescription>
            Last 50 AI recommendation requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {logs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No API calls logged yet
              </div>
            ) : (
              <div className="space-y-2">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">@{log.username || 'unknown'}</span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(log.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary mr-2">
                          {formatModelName(log.model)}
                        </span>
                        {log.total_tokens.toLocaleString()} tokens
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm font-medium">
                        ${Number(log.estimated_cost_usd).toFixed(5)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {log.prompt_tokens.toLocaleString()} → {log.completion_tokens.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
